import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";

/*
 * Saved reports ("My reports", owner 2026-10-25): backend `api/custom-reports`.
 * `spec` = the ReportSpec recipe of an AI-made report (features/reports/custom/reportSpec.ts)
 * or a saved standard report view (features/reports/saved/savedView.ts, 2026-10-26);
 * numbers are always computed in the browser.
 */

export type CustomReportRow = {
  id: string;
  name: string;
  description?: string | null;
  request?: string | null;
  source: string;
  shared: boolean;
  /** Created by the signed-in person (may change / delete it). */
  mine: boolean;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  /** "ai" = made with AI; "standard" = a standard report saved with its filters. */
  kind?: "ai" | "standard";
  /** Standard report it is based on (e.g. "time"). */
  basedOn?: string | null;
  /** Saved filters in words ("Last month", "2 team members"). */
  labels?: string[];
};

export type CustomReport = CustomReportRow & {
  spec: unknown;
  insight?: string | null;
  insightAt?: string | null;
};

export type CustomReportSave = {
  orgId: string;
  name?: string;
  description?: string;
  request?: string;
  spec?: unknown;
  insight?: string;
  shared?: boolean;
};

export const customReportsSlice = createApi({
  reducerPath: "customReportsSlice",
  tagTypes: ["CustomReports", "CustomReport"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCustomReports: build.query<
      { available: boolean; reports: CustomReportRow[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({ url: "api/custom-reports", params: { orgId } }),
      providesTags: ["CustomReports"],
    }),
    getCustomReport: build.query<CustomReport, { id: string; orgId: string }>({
      query: ({ id, orgId }) => ({
        url: `api/custom-reports/${id}`,
        params: { orgId },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "CustomReport", id }],
    }),
    createCustomReport: build.mutation<CustomReport, CustomReportSave>({
      query: (body) => ({ url: "api/custom-reports", method: "POST", body }),
      invalidatesTags: ["CustomReports"],
    }),
    updateCustomReport: build.mutation<
      CustomReport,
      CustomReportSave & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `api/custom-reports/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "CustomReports",
        { type: "CustomReport", id },
      ],
    }),
    deleteCustomReport: build.mutation<void, { id: string; orgId: string }>({
      query: ({ id, orgId }) => ({
        url: `api/custom-reports/${id}`,
        method: "DELETE",
        params: { orgId },
      }),
      invalidatesTags: ["CustomReports"],
    }),
  }),
});

export const {
  useGetCustomReportsQuery,
  useGetCustomReportQuery,
  useCreateCustomReportMutation,
  useUpdateCustomReportMutation,
  useDeleteCustomReportMutation,
} = customReportsSlice;
